<template>
  <div class="news-detail-container">
    <HomepageBanner
      :images="bannerImages"
      :auto-play-interval="5000"
      :show-indicators="true"
      :show-navigation="true"
    />

    <div class="news-detail-page">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>ລໍຖ້າແປັບໜຶ່ງເດີ້...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <svg
          class="error-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h3>ອຸ້ຍ! ເກີດຂໍ້ຜິດພາດ</h3>
        <p>{{ error.message || "ບໍ່ສາມາດໂຫຼດຂ່າວໄດ້" }}</p>
        <button @click="refetch" class="retry-button">ລອງອີກເທື່ອໜຶ່ງ</button>
      </div>

      <!-- Content -->
      <article v-else-if="news" class="news-article">
        <!-- Header -->
        <header class="news-header">
          <h1 class="news-title">{{ news.title }}</h1>

          <div class="news-meta">
            <!-- category -->
            <span v-if="news.category" class="category-badge">
              {{ news.category.name }}
            </span>

            <time class="news-date" :datetime="news.published_at">
              <svg
                class="icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {{ formatDate(news.published_at) }}
            </time>
          </div>
        </header>

        <!-- Featured Image -->
        <div v-if="news.featured_image" class="featured-image-container">
          <img
            :src="news.featured_image"
            :alt="news.title"
            class="featured-image"
            @error="handleImageError"
          />
        </div>

        <!-- Content Sections -->
        <div class="news-body">
          <!-- ─── new_content renderer (แทน news_sections) ─── -->
          <div
            v-if="news.new_content && news.new_content.length"
            class="article-content"
          >
            <template v-for="(block, index) in news.new_content" :key="index">
              <p
                v-if="block.type === 'paragraph'"
                class="content-paragraph"
                v-html="block.text"
              ></p>

              <p
                v-else-if="block.type === 'lightparagraph'"
                class="content-lightparagraph"
                v-html="block.text"
              ></p>

              <p
                v-else-if="block.type === 'boldTitle'"
                class="content-bold-title"
              >
                {{ block.text }}
              </p>

              <p
                v-else-if="block.type === 'semiboldTitle'"
                class="content-semibold-title"
              >
                {{ block.text }}
              </p>

              <ul
                v-else-if="block.type === 'bulletList'"
                class="content-bullet-list"
              >
                <li
                  v-for="(item, i) in block.items"
                  :key="i"
                  class="bullet-item"
                >
                  <span class="bullet-dot"></span>
                  <span class="bullet-text">{{ item }}</span>
                </li>
              </ul>

              <ol
                v-else-if="block.type === 'numberedList'"
                class="content-numbered-list"
              >
                <li
                  v-for="(item, i) in block.items"
                  :key="i"
                  class="numbered-item"
                >
                  <span class="numbered-dot">{{ i + 1 }}</span>
                  <span class="numbered-text">{{ item }}</span>
                </li>
              </ol>

              <p
                v-else-if="block.type === 'underlineParagraph'"
                class="content-underline-paragraph"
              >
                {{ block.text }}
              </p>

              <div
                v-else-if="block.type === 'image'"
                class="content-image-wrapper"
              >
                <img
                  :src="block.src"
                  :alt="block.alt || ''"
                  class="content-image"
                />
                <p v-if="block.caption" class="content-image-caption">
                  {{ block.caption }}
                </p>
              </div>
            </template>
          </div>
        </div>

        <!-- Gallery -->
        <!-- Gallery -->
        <div
          v-if="news.news_images && news.news_images.length"
          class="news-gallery"
        >
          <!-- Grid Layout (≤ 3 images) - เก็บไว้เหมือนเดิม -->
          <div v-if="news.news_images.length <= 3" class="gallery-grid">
            <figure
              v-for="(img, index) in news.news_images"
              :key="img.id"
              class="gallery-item"
              @click="openLightbox(index)"
            >
              <img
                :src="img.image_url"
                :alt="img.alt_text || img.caption || news.title"
                loading="lazy"
                @error="handleImageError"
              />
              <figcaption v-if="img.caption" class="gallery-caption">
                {{ img.caption }}
              </figcaption>
            </figure>
          </div>

          <!-- ✅ NEW: Gallery Slider Layout (> 3 images) -->
          <div v-else class="gallery-slider-container">
            <!-- Main Image Display -->
            <div class="main-image-container">
              <button
                class="slider-nav prev"
                @click="prevGalleryImage"
                :disabled="currentGalleryIndex === 0"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <div
                class="main-image-wrapper"
                @click="openLightbox(currentGalleryIndex)"
              >
                <img
                  :src="news.news_images[currentGalleryIndex]?.image_url"
                  :alt="news.news_images[currentGalleryIndex]?.alt_text"
                  class="main-image"
                  @error="handleImageError"
                />
              </div>

              <button
                class="slider-nav next"
                @click="nextGalleryImage"
                :disabled="currentGalleryIndex === news.news_images.length - 1"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            <!-- Caption -->
            <div
              v-if="news.news_images[currentGalleryIndex]?.caption"
              class="gallery-caption-main"
            >
              {{ news.news_images[currentGalleryIndex].caption }}
            </div>

            <!-- Thumbnail Strip -->
            <div class="thumbnail-strip">
              <div class="thumbnail-container">
                <button
                  v-for="(img, index) in news.news_images"
                  :key="img.id"
                  class="thumbnail-item"
                  :class="{ active: currentGalleryIndex === index }"
                  @click="currentGalleryIndex = index"
                >
                  <img
                    :src="img.image_url"
                    :alt="img.alt_text || `Thumbnail ${index + 1}`"
                    @error="handleImageError"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      <!-- No Data State -->
      <div v-else class="no-data-container">
        <svg
          class="no-data-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3>ບໍ່ພົບຂ່າວ</h3>
        <p>ຂໍ້ມູນຂ່າວທີ່ທ່ານຊອກຫາບໍ່ມີໃນລະບົບ</p>
        <button @click="goBack" class="back-button">ກັບຄືນ</button>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <Teleport to="body">
      <div v-if="lightboxOpen" class="lightbox-overlay" @click="closeLightbox">
        <button class="lightbox-close" @click="closeLightbox">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <button
          class="lightbox-prev"
          @click.stop="prevImage"
          v-if="news.news_images.length > 1"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <div class="lightbox-content" @click.stop>
          <img
            :src="news.news_images[currentImageIndex]?.image_url"
            :alt="news.news_images[currentImageIndex]?.alt_text"
          />
          <p
            v-if="news.news_images[currentImageIndex]?.caption"
            class="lightbox-caption"
          >
            {{ news.news_images[currentImageIndex].caption }}
          </p>
        </div>

        <button
          class="lightbox-next"
          @click.stop="nextImage"
          v-if="news.news_images.length > 1"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, defineProps, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRouter } from "vue-router";
import gql from "graphql-tag";
import HomepageBanner from "@/components/common/PageBanner.vue";

const props = defineProps({
  slug: { type: String, required: true },
});

const router = useRouter();

// Banner
// เพิ่ม Query HomepageBanner นี้
const GET_BANNERS = gql`
  query GetPageBanners($slug: String!) {
    HomepageBanner_pages(where: { page_slug: { _eq: $slug } }) {
      id
      title
      banners(
        where: { is_active: { _eq: true } }
        order_by: { sort_order: asc }
      ) {
        id
        image_url
        title
        description
      }
    }
  }
`;

// State
const lightboxOpen = ref(false);
const currentImageIndex = ref(0);
const currentGalleryIndex = ref(0);

/* ================= GRAPHQL ================= */
const GET_NEWS_DETAIL = gql`
  query GetNewsDetail($slug: String!) {
    news(limit: 1, where: { slug: { _eq: $slug } }) {
      id
      title
      slug
      featured_image
      published_at
      new_content
      news_images

      category {
        id
        name
        slug
      }
    }
  }
`;

// Banner Images
// เพิ่ม Query execution
const { result: bannerResult } = useQuery(
  GET_BANNERS,
  { slug: "News-details" }, // 👈 page_slug ของหน้านี้
  { fetchPolicy: "network-only" }
);

// เปลี่ยน bannerImages จาก ref hardcode เป็น ref ว่างๆ
const bannerImages = ref([]);

// เพิ่ม watch ดึงข้อมูล banner
watch(
  () => bannerResult.value?.HomepageBanner_pages,
  (newData) => {
    if (newData && newData.length > 0) {
      const rawBanners = newData[0].banners;
      bannerImages.value = rawBanners.map((banner) => ({
        url: banner.image_url,
        alt: banner.title || "Page Banner",
        title: banner.title || "",
        description: banner.description || "",
      }));
    }
  },
  { immediate: true }
);

const { result, loading, error, refetch } = useQuery(
  GET_NEWS_DETAIL,
  () => ({ slug: props.slug }),
  { fetchPolicy: "cache-and-network" }
);

/* ================= DATA ================= */
const news = computed(() => {
  const n = result.value?.news?.[0];
  if (!n) return null;

  const flattenedImages = (() => {
    try {
      const parsed =
        typeof n.news_images === "string"
          ? JSON.parse(n.news_images)
          : n.news_images;
      const arr = Array.isArray(parsed) ? parsed : [];
      return arr
        .map((img) => ({
          id: img.order || Math.random(),
          image_url: img.url,
          caption: img.caption || "",
          alt_text: img.alt || "",
          order: img.order || 0,
        }))
        .sort((a, b) => a.order - b.order);
    } catch {
      return [];
    }
  })();

  return {
    ...n,
    news_images: flattenedImages, // ✅ ใช้ images ที่ flatten แล้ว
    new_content: (() => {
      try {
        const parsed =
          typeof n.new_content === "string"
            ? JSON.parse(n.new_content)
            : n.new_content;
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    })(),
  };
});

/* ================= METHODS ================= */
const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) return dateString;

  const monthsLao = [
    "ມັງກອນ",
    "ກຸມພາ",
    "ມີນາ",
    "ເມສາ",
    "ພຶດສະພາ",
    "ມິຖຸນາ",
    "ກໍລະກົດ",
    "ສິງຫາ",
    "ກັນຍາ",
    "ຕຸລາ",
    "ພະຈິກ",
    "ທັນວາ",
  ];

  const day = date.getDate();
  const month = monthsLao[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

const handleImageError = (event) => {
  event.target.src =
    "https://sokxaylifeinsurance.com/images/news/00 handleImageError/VTE.jpg";
};

const goBack = () => {
  router.push("/news");
};

// Lightbox Functions
const openLightbox = (index) => {
  currentImageIndex.value = index;
  lightboxOpen.value = true;
  document.body.style.overflow = "hidden";
};

const closeLightbox = () => {
  lightboxOpen.value = false;
  document.body.style.overflow = "";
};

const nextImage = () => {
  if (currentImageIndex.value < news.value.news_images.length - 1) {
    currentImageIndex.value++;
  } else {
    currentImageIndex.value = 0;
  }
};

const prevImage = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--;
  } else {
    currentImageIndex.value = news.value.news_images.length - 1;
  }
};
const nextGalleryImage = () => {
  if (currentGalleryIndex.value < news.value.news_images.length - 1) {
    currentGalleryIndex.value++;
  }
};

const prevGalleryImage = () => {
  if (currentGalleryIndex.value > 0) {
    currentGalleryIndex.value--;
  }
};
</script>

<style scoped>
.news-detail-container {
  min-height: 100vh;
  background-color: #f5f8fa;
}

.news-detail-page {
  max-width: 56rem;
  margin: 0 auto;
  padding: 2rem 1rem;
  font-family: "Noto Sans Lao", sans-serif;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1.5rem;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ea2127;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 2rem;
}

.error-icon {
  width: 4rem;
  height: 4rem;
  color: #ea2127;
  margin-bottom: 1.5rem;
}

.error-container h3 {
  font-size: 1.5rem;
  color: #1e293b;
  margin-bottom: 0.75rem;
}

.error-container p {
  color: #64748b;
  margin-bottom: 1.5rem;
}

.retry-button {
  padding: 0.75rem 2rem;
  background: #ea2127;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-button:hover {
  background: #c91c22;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(234, 33, 39, 0.3);
}

/* Article Header */
.news-header {
  text-align: center;
  margin-bottom: 1rem;
}

.category-badge {
  display: inline-block;
  padding: 0.5rem 1.5rem;
  background: #ffffff;
  color: #323d47;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(196, 196, 196, 0.3);
}

.news-title {
  font-size: 2.5rem;
  color: #1e293b;
  margin: 0 0 3rem 0;
  line-height: 1.3;
  font-weight: 700;
}
.news-date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #323d47;
}
.news-date .icon {
  width: 1em;
  height: 1em;
  flex-shrink: 0;
}
.news-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
}

.news-meta .icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  stroke-width: 2;
}

/* Featured Image */
.featured-image-container {
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-height: 32rem;
}

.featured-image {
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease;
}

.featured-image:hover {
  transform: scale(1.02);
}

/* News Body */
.news-body {
  margin-top: 3rem;
}

/* ─── Content Blocks ─────── */
.article-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.content-paragraph {
  font-size: 1rem;
  line-height: 1.85;
  color: #344873;
  margin: 0 0 20px;
}

.content-paragraph a,
.content-lightparagraph a {
  color: #188dd5;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: opacity 0.2s ease;
}

.content-paragraph a:hover,
.content-lightparagraph a:hover {
  opacity: 0.75;
}

.content-lightparagraph {
  font-size: 1rem;
  line-height: 1.85;
  color: #188dd5;
  margin: 0 0 20px;
}

.content-bold-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #344873;
  margin: 28px 0 14px;
  padding-left: 14px;
  border-left: 4px solid #ea2127;
  line-height: 1.45;
}

.content-semibold-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #344873;
  margin: 0 0 14px;
  line-height: 1.85;
}

.content-bullet-list {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bullet-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.bullet-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #344873;
  margin-top: 8px;
}

.bullet-text {
  font-size: 1rem;
  line-height: 1.75;
  color: #344873;
}

.content-numbered-list {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.numbered-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.numbered-dot {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  color: #344873;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.numbered-text {
  font-size: 1rem;
  line-height: 1.75;
  color: #344873;
}

.content-underline-paragraph {
  font-size: 1.1rem;
  line-height: 1.85;
  color: #344873;
  margin: 0 0 20px;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.content-image-wrapper {
  margin: 20px 0 28px;
  border-radius: 0.75rem;
  overflow: hidden;
  isolation: isolate;
  transform: translateZ(0);
}

.content-image-wrapper img {
  border-radius: 0.75rem;
  display: block;
  width: 100%;
  height: auto;
}

.content-image {
  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
}

.content-image-caption {
  font-size: 0.82rem;
  color: #6b7a99;
  text-align: center;
  margin: 0.75rem 0 0;
  font-style: italic;
}

/* Gallery Swiper */
.gallery-swiper-container {
  position: relative;
  padding: 0 3rem;
  margin: 0 -1rem;
}

.gallery-swiper {
  padding-bottom: 3rem;
}

.gallery-item.swiper-item {
  height: auto;
}

/* Swiper Navigation Buttons */
.gallery-swiper :deep(.swiper-button-next),
.gallery-swiper :deep(.swiper-button-prev) {
  width: 3rem;
  height: 3rem;
  background: white;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: #ea2127;
}

.gallery-swiper :deep(.swiper-button-next):after,
.gallery-swiper :deep(.swiper-button-prev):after {
  font-size: 1.25rem;
  font-weight: 900;
}

.gallery-swiper :deep(.swiper-button-next):hover,
.gallery-swiper :deep(.swiper-button-prev):hover {
  background: #ea2127;
  color: white;
  transform: scale(1.1);
}

.gallery-swiper :deep(.swiper-button-disabled) {
  opacity: 0.3;
}

/* Swiper Pagination */
.gallery-swiper :deep(.swiper-pagination) {
  bottom: 0;
}

.gallery-swiper :deep(.swiper-pagination-bullet) {
  width: 0.75rem;
  height: 0.75rem;
  background: #cbd5e1;
  opacity: 1;
  transition: all 0.3s ease;
}

.gallery-swiper :deep(.swiper-pagination-bullet-active) {
  background: #ea2127;
  width: 2rem;
  border-radius: 0.375rem;
}

/* Responsive - Tablet */
@media (max-width: 64rem) {
  .gallery-swiper-container {
    padding: 0 2.5rem;
  }

  .gallery-swiper :deep(.swiper-button-next),
  .gallery-swiper :deep(.swiper-button-prev) {
    width: 2.5rem;
    height: 2.5rem;
  }

  .gallery-swiper :deep(.swiper-button-next):after,
  .gallery-swiper :deep(.swiper-button-prev):after {
    font-size: 1rem;
  }
}

/* Responsive - Mobile */
@media (max-width: 48rem) {
  .gallery-swiper-container {
    padding: 0 2rem;
    margin: 0;
  }

  .gallery-swiper :deep(.swiper-button-next),
  .gallery-swiper :deep(.swiper-button-prev) {
    width: 2rem;
    height: 2rem;
  }

  .gallery-swiper :deep(.swiper-button-next):after,
  .gallery-swiper :deep(.swiper-button-prev):after {
    font-size: 0.875rem;
  }

  .gallery-swiper :deep(.swiper-button-next) {
    right: 0.5rem;
  }

  .gallery-swiper :deep(.swiper-button-prev) {
    left: 0.5rem;
  }
}

/* Gallery */
.news-gallery {
  margin: 4rem 0;
}

.gallery-title {
  font-size: 1.75rem;
  color: #1e293b;
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: center;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: 1.5rem;
}

.gallery-item {
  margin: 0;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.gallery-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 24px rgba(234, 33, 39, 0.2);
}

.gallery-item img {
  width: 100%;
  height: 14rem;
  object-fit: cover;
  display: block;
}

.gallery-caption {
  padding: 1rem;
  font-size: 0.875rem;
  color: #475569;
  background: #f8fafc;
  text-align: center;
}
/* Gallery Slider Styles */
.gallery-slider-container {
  margin: 3rem 0;
}

.main-image-container {
  position: relative;
  margin-bottom: 1rem;
  border-radius: 1rem;
  overflow: hidden;
  background: #f8fafc;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.main-image-wrapper {
  cursor: pointer;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.main-image-wrapper:hover .main-image {
  transform: scale(1.05);
}

.slider-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 3rem;
  height: 3rem;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #1e293b;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.slider-nav.prev {
  left: 1.5rem;
}

.slider-nav.next {
  right: 1.5rem;
}

.slider-nav:hover:not(:disabled) {
  background: #ea2127;
  color: white;
  transform: translateY(-50%) scale(1.1);
}

.slider-nav:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.slider-nav svg {
  width: 1.5rem;
  height: 1.5rem;
}

/* Thumbnail Strip */
.thumbnail-strip {
  overflow-x: auto;
  padding: 0.5rem 0;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.thumbnail-strip::-webkit-scrollbar {
  height: 0.5rem;
}

.thumbnail-strip::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 0.25rem;
}

.thumbnail-strip::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 0.25rem;
}

.thumbnail-strip::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.thumbnail-container {
  display: flex;
  gap: 1rem;
  padding: 0.25rem;
}

.thumbnail-item {
  flex-shrink: 0;
  width: 6rem;
  height: 6rem;
  border: 3px solid transparent;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
  padding: 0;
}

.thumbnail-item:hover {
  border-color: #ea2127;
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(234, 33, 39, 0.3);
}

.thumbnail-item.active {
  border-color: #ea2127;
  box-shadow: 0 0 0 2px rgba(234, 33, 39, 0.2);
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.gallery-caption-main {
  text-align: center;
  margin-bottom: 1rem;
  color: #475569;
  font-size: 1rem;
}

/* Responsive - Tablet */
@media (max-width: 64rem) {
  .slider-nav {
    width: 2.5rem;
    height: 2.5rem;
  }

  .slider-nav.prev {
    left: 1rem;
  }

  .slider-nav.next {
    right: 1rem;
  }

  .thumbnail-item {
    width: 5rem;
    height: 5rem;
  }
}

/* Responsive - Mobile */
@media (max-width: 48rem) {
  .main-image-wrapper {
    aspect-ratio: 4/3;
  }

  .slider-nav {
    width: 2rem;
    height: 2rem;
  }

  .slider-nav.prev {
    left: 0.5rem;
  }

  .slider-nav.next {
    right: 0.5rem;
  }

  .slider-nav svg {
    width: 1rem;
    height: 1rem;
  }

  .thumbnail-item {
    width: 4rem;
    height: 4rem;
  }

  .gallery-caption-main {
    font-size: 0.875rem;
  }
}

/* No Data State */
.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 2rem;
}

.no-data-icon {
  width: 4rem;
  height: 4rem;
  color: #cbd5e1;
  margin-bottom: 1.5rem;
}

.no-data-container h3 {
  font-size: 1.5rem;
  color: #1e293b;
  margin-bottom: 0.75rem;
}

.no-data-container p {
  color: #64748b;
  margin-bottom: 1.5rem;
}

/* Lightbox */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
}

.lightbox-close {
  position: absolute;
  top: 2rem;
  right: 2rem;
  width: 3rem;
  height: 3rem;
}

.lightbox-close:hover {
  background: #ea2127;
  color: white;
  transform: scale(1.1);
}
.lightbox-close svg {
  width: 1.5rem;
  height: 1.5rem;
}
.lightbox-prev,
.lightbox-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 3rem;
  height: 3rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #1e293b;
}
.lightbox-prev {
  left: 2rem;
}
.lightbox-next {
  right: 2rem;
}
.lightbox-prev:hover,
.lightbox-next:hover {
  background: #ea2127;
  color: white;
  transform: translateY(-50%) scale(1.1);
}
.lightbox-prev svg,
.lightbox-next svg {
  width: 1.5rem;
  height: 1.5rem;
}
.lightbox-content {
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.lightbox-content img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 0.5rem;
}
.lightbox-caption {
  color: white;
  font-size: 1rem;
  margin-top: 1rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
}
/* Responsive - Tablet */
@media (max-width: 64rem) {
  .news-title {
    font-size: 2rem;
  }

  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  }
}
/* Responsive - Mobile */
@media (max-width: 48rem) {
  .news-detail-page {
    padding: 1.5rem 1rem;
  }
  .news-title {
    font-size: 1.75rem;
  }
  .news-meta {
    justify-content: space-between;
    gap: 0.75rem;
  }

  .gallery-grid {
    grid-template-columns: 1fr;
  }
  .share-buttons {
    flex-direction: column;
  }
  .share-btn {
    width: 100%;
    justify-content: center;
  }
  .lightbox-close {
    top: 1rem;
    right: 1rem;
    width: 2.5rem;
    height: 2.5rem;
  }
  .lightbox-prev,
  .lightbox-next {
    width: 2.5rem;
    height: 2.5rem;
  }
  .lightbox-prev {
    left: 0.5rem;
  }
  .lightbox-next {
    right: 0.5rem;
  }
}
/* Small Mobile */
@media (max-width: 30rem) {
  .news-title {
    font-size: 1.5rem;
  }

  .section-item {
    padding: 1rem;
  }
}
</style>